import React, { useState, useEffect } from 'react';
import axios from '../../axiosConfig';
import '../../styles/DogSurvey.css';
import { IoChevronDownOutline, IoChevronUpOutline } from "react-icons/io5";

const DogSurveysList = ({ surveys, dogId }) => {
    const [expandedSurvey, setExpandedSurvey] = useState(null);
    const [surveyResponses, setSurveyResponses] = useState({});
    const [loadingResponses, setLoadingResponses] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (expandedSurvey) {
            fetchSurveyResponses(expandedSurvey, dogId);
        }
    }, [expandedSurvey, dogId]);

    const fetchSurveyResponses = async (surveyId, dogId) => {
        if (surveyResponses[surveyId]) {
            // Already fetched
            return;
        }

        setLoadingResponses(true);
        setError('');

        try {
            const response = await axios.get(`/api/v1/survey-response/dog/${dogId}/survey/${surveyId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });

            setSurveyResponses(prev => ({
                ...prev,
                [surveyId]: response.data
            }));
        } catch (err) {
            console.error(`Error fetching responses for survey ${surveyId}:`, err);
            setSurveyResponses(prev => ({
                ...prev,
                [surveyId]: []
            }));
        } finally {
            setLoadingResponses(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Brak daty';
        const date = new Date(dateString);
        return date.toLocaleDateString('pl-PL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const toggleSurveyExpand = (surveyId) => {
        setExpandedSurvey(expandedSurvey === surveyId ? null : surveyId);
    };

    const getResponseForQuestion = (questionId, responses) => {
        if (!responses || responses.length === 0) return null;
        return responses.find(response => response.questionId === questionId);
    };

    const renderResponseValue = (response, questionType) => {
        if (!response) return <span className="dog-survey-no-response">Brak odpowiedzi</span>;

        switch (questionType) {
            case 'TEXT':
                return response.textAnswer || <span className="dog-survey-no-response">Brak odpowiedzi</span>;
            case 'DATE':
                return response.dateAnswer ? formatDate(response.dateAnswer) : <span className="dog-survey-no-response">Brak odpowiedzi</span>;
            case 'SINGLE_CHOICE':
                return response.singleChoiceAnswer || <span className="dog-survey-no-response">Brak odpowiedzi</span>;
            case 'MULTIPLE_CHOICE':
                return response.multipleChoiceAnswer && response.multipleChoiceAnswer.length > 0 ? (
                    <ul className="dog-survey-multiple-choice-list">
                        {response.multipleChoiceAnswer.map((option, idx) => (
                            <li key={idx}>{option}</li>
                        ))}
                    </ul>
                ) : <span className="dog-survey-no-response">Brak odpowiedzi</span>;
            case 'CHOICE_WITH_TEXT':
                return (
                    <div className="dog-survey-choice-with-text-response">
                        {response.singleChoiceAnswer && (
                            <div className="dog-survey-selected-option">Wybór: {response.singleChoiceAnswer}</div>
                        )}
                        {response.textAnswer && (
                            <div className="dog-survey-custom-text">Tekst: {response.textAnswer}</div>
                        )}
                        {!response.singleChoiceAnswer && !response.textAnswer && (
                            <span className="dog-survey-no-response">Brak odpowiedzi</span>
                        )}
                    </div>
                );
            default:
                return <span className="dog-survey-no-response">Brak odpowiedzi</span>;
        }
    };

    if (!surveys || surveys.length === 0) {
        return (
            <div className="dog-surveys-no-message">
                <p>Brak przypisanych ankiet dla tego psa.</p>
            </div>
        );
    }

    return (
        <div className="dog-surveys-container">
            <div className="dog-surveys-list">
                {surveys.map(survey => (
                    <div
                        key={survey.id}
                        className={`dog-survey-item ${expandedSurvey === survey.id ? 'dog-survey-expanded' : ''}`}
                        onClick={() => toggleSurveyExpand(survey.id)}
                    >
                        <div className="dog-survey-header">
                            <div className="dog-survey-info">
                                <h3 className="dog-survey-name">{survey.title}</h3>
                                <div className="dog-survey-meta">
                                    {survey.createdAt && (
                                        <span className="dog-survey-date-badge">
                                            Utworzono: {formatDate(survey.createdAt)}
                                        </span>
                                    )}
                                    <span className="dog-survey-questions-badge">
                                        Liczba pytań: {survey.questions ? survey.questions.length : 0}
                                    </span>
                                </div>
                            </div>
                            <div className="dog-survey-actions">
                                {expandedSurvey === survey.id ? (
                                    <IoChevronUpOutline
                                        size={20}
                                        className="dog-survey-expand-icon dog-survey-expanded"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleSurveyExpand(survey.id);
                                        }}
                                        title="Ukryj szczegóły"
                                    />
                                ) : (
                                    <IoChevronDownOutline
                                        size={20}
                                        className="dog-survey-expand-icon"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleSurveyExpand(survey.id);
                                        }}
                                        title="Pokaż szczegóły"
                                    />
                                )}
                            </div>
                        </div>

                        {expandedSurvey === survey.id && (
                            <div className="dog-survey-details">
                                <div className="dog-survey-details-content">
                                    {loadingResponses ? (
                                        <div className="dog-survey-loading-responses">
                                            <p>Ładowanie odpowiedzi...</p>
                                        </div>
                                    ) : error ? (
                                        <div className="dog-survey-error-message">
                                            <p>{error}</p>
                                        </div>
                                    ) : (
                                        <div className="dog-survey-questions-list">
                                            {survey.questions
                                                .sort((a, b) => a.questionIndex - b.questionIndex)
                                                .map((question, index) => {
                                                    const response = getResponseForQuestion(
                                                        question.id,
                                                        surveyResponses[survey.id]
                                                    );

                                                    return (
                                                        <div key={question.id} className="dog-survey-question-item">
                                                            <div className="dog-survey-question-header">
                                                                <div className="dog-survey-question-number">{index + 1}.</div>
                                                                <div className="dog-survey-question-text">{question.questionText}</div>
                                                                <div className="dog-survey-question-type">
                                                                    {question.questionType === 'TEXT' && 'Tekst'}
                                                                    {question.questionType === 'DATE' && 'Data'}
                                                                    {question.questionType === 'SINGLE_CHOICE' && 'Pojedynczy wybór'}
                                                                    {question.questionType === 'MULTIPLE_CHOICE' && 'Wielokrotny wybór'}
                                                                    {question.questionType === 'CHOICE_WITH_TEXT' && 'Wybór + tekst'}
                                                                </div>
                                                            </div>

                                                            <div className="dog-survey-question-content">
                                                                {(question.questionType === 'SINGLE_CHOICE' ||
                                                                        question.questionType === 'MULTIPLE_CHOICE' ||
                                                                        question.questionType === 'CHOICE_WITH_TEXT') &&
                                                                    question.answerOptions &&
                                                                    question.answerOptions.length > 0 && (
                                                                        <div className="dog-survey-answer-options">
                                                                            <div className="dog-survey-options-label">Dostępne opcje:</div>
                                                                            <ul>
                                                                                {question.answerOptions.map((option, idx) => (
                                                                                    <li key={idx}>{option}</li>
                                                                                ))}
                                                                            </ul>
                                                                        </div>
                                                                    )}

                                                                <div className="dog-survey-response-section">
                                                                    <div className="dog-survey-response-label">Odpowiedź:</div>
                                                                    <div className="dog-survey-response-value">
                                                                        {renderResponseValue(response, question.questionType)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DogSurveysList;